import { FileTextIcon, TrashIcon } from '@slal/ui/icons';
import { useEffect } from 'react';
import { useImageUpload } from './hooks';
import {
    DeleteButton,
    FileUpload,
    Image,
    ImagePreview,
    ImagePreviewWrapper,
    ImageScaling,
    ScalingHelpText,
    Separator,
    Slider,
    SliderLabel,
    UploadButton,
    UploadMessage,
    Wrapper,
} from './styles';

interface ImageUploaderProps {
    onChange?: (value?: string) => void;
    onScaleChange?: (value?: number) => void;
    id?: string;
    imageUploadHelpText?: string;
    scalingHelpText?: string;
    maximumFileSize?: number;
    resizedImageSize?: number;
    imageDataURL?: string;
    imageScale?: number;
}

export const ImageUpload = ({
    onChange,
    onScaleChange,
    id,
    imageUploadHelpText = 'Drag a JPG, PNG or SVG under 5mb here to upload your logo.',
    scalingHelpText,
    maximumFileSize,
    resizedImageSize,
    imageDataURL,
    imageScale = 100,
    ...otherProps
}: ImageUploaderProps) => {
    const {
        sliderValue,
        handleSliderChange,
        handleSliderAfterChange,
        getRootProps,
        getInputProps,
        resetImageUpload,
        setSliderValue,
        dataURL,
    } = useImageUpload({
        onChange,
        onScaleChange,
        maximumFileSize,
        resizedImageSize,
        imageDataURL,
        imageScale,
    });

    useEffect(() => {
        setSliderValue(imageScale);
    }, [imageScale]);

    return (
        <Wrapper {...otherProps}>
            {dataURL ? (
                <ImagePreviewWrapper>
                    <ImagePreview>
                        <DeleteButton
                            type="button"
                            title="Remove logo"
                            onClick={() => {
                                resetImageUpload();
                                onChange?.(undefined);
                                onScaleChange?.(100);
                            }}
                        >
                            <TrashIcon />
                        </DeleteButton>
                        <Image alt="Logo" src={dataURL} style={{ width: `${50 * (sliderValue / 100)}%` }} />
                    </ImagePreview>
                    <ImageScaling>
                        <SliderLabel>
                            Logo scale <strong>{sliderValue}%</strong>
                        </SliderLabel>
                        <ScalingHelpText>{scalingHelpText}</ScalingHelpText>
                        <Slider
                            value={sliderValue || imageScale}
                            min={50}
                            max={150}
                            className="horizontal-slider"
                            thumbClassName="slider-thumb"
                            trackClassName="slider-track"
                            onChange={value => handleSliderChange(value as number)}
                            onAfterChange={value => {
                                handleSliderAfterChange(value as number);
                                onScaleChange?.(value as number);
                            }}
                            renderThumb={(props, state) => {
                                const rest = props as unknown as React.HTMLAttributes<HTMLDivElement>;
                                return (
                                    <div {...rest} aria-label={`Adjust logo scale, current value ${state.valueNow}%`} />
                                );
                            }}
                        />
                    </ImageScaling>
                </ImagePreviewWrapper>
            ) : (
                <FileUpload {...getRootProps()}>
                    <input id={id} {...getInputProps()} />
                    <FileTextIcon />
                    <UploadMessage>{imageUploadHelpText}</UploadMessage>
                    <Separator>OR</Separator>
                    <UploadButton variant="outline">Browse files</UploadButton>
                </FileUpload>
            )}
        </Wrapper>
    );
};
